<template>
  <div
    v-if="!$params.iframe"
    class="container-fluid-lg relative flex flex-col gap-x-4 gap-y-12 sm:grid sm:grid-cols-2 sm:grid-rows-2 md:grid-cols-3"
  >
    <div
      id="inspirations"
      class="flex flex-col items-center justify-center gap-6 sm:items-start"
    >
      <h2 class="text-2xl font-medium">{{ $t(`${trPrefix}title`) }}</h2>
      <span class="text-center text-base sm:text-left">{{
        $t(`${trPrefix}subtitle`)
      }}</span>
      <WebccButton
        size="lg"
        class="min-w-3/4 sm:min-w-0"
        variant="cta"
        @click="toAll"
      >
        <span>{{ $t(`${trPrefix}button`) }}</span>
      </WebccButton>
    </div>
    <WebccLink
      v-for="(item, i) in block.content.teasers"
      :key="i"
      v-track:impression="{
        event: `teaser:holidayIdeasElementImpression`,
        params: {
          id: 'Home Teaser ' + 4,
          creative: item.virtpath,
          position: `${4}/${i}`,
        },
      }"
      :class="i >= 3 ? 'sm:hidden md:block' : 'sm:block'"
      :anchor="item.link?.anchor"
      :to="item.link?.href"
      class="hidden h-full cursor-pointer rounded-lg border shadow-md"
      @click="
        useTracking().handlers?.teaser.holidayIdeasElementClick({
          id: 'Home Teaser ' + 4,
          creative: item.virtpath,
          position: `${4}/${i}`,
        })
      "
    >
      <figure class="flex flex-row justify-center rounded-lg">
        <img
          loading="lazy"
          class="w-full rounded-lg object-cover"
          :src="item.image + '/' + transformation"
          :alt="item.alt"
          :title="item.title"
        />
      </figure>
      <div class="flex flex-col items-start px-4 py-2">
        <h3 class="text-xl font-medium">{{ item.title }}</h3>
        <span class="text-md">{{ item.subtitle }}</span>
      </div>
    </WebccLink>
    <div class="relative overflow-x-hidden">
      <div
        ref="scroller"
        :class="{ 'overflow-x-scroll': !(atStart && atEnd) }"
        class="hide-scrollbar relative flex flex-grow snap-x snap-proximity gap-4 scroll-smooth shadow-md sm:hidden"
        @scroll="scroll"
      >
        <WebccLink
          v-for="(item, i) in block.content.teasers"
          :key="i"
          ref="scrolltarget"
          v-track:impression="{
            event: `teaser:holidayIdeasElementImpression`,
            params: {
              id: 'Home Teaser ' + 4,
              creative: item.virtpath,
              position: `${4}/${i}`,
            },
          }"
          :anchor="item.link?.anchor"
          :to="item.link?.href"
          style="scroll-ml-6 scroll-snap-align: start"
          class="min-w-[100%] cursor-pointer snap-start rounded-lg border shadow-md"
          @click="
            useTracking().handlers?.teaser.holidayIdeasElementClick({
              id: 'Home Teaser ' + 4,
              creative: item.virtpath,
              position: `${4}/${i}`,
            })
          "
        >
          <figure class="flex w-full flex-row justify-center rounded-lg">
            <img
              loading="lazy"
              class="w-full rounded-lg object-cover"
              :src="item.image + '/' + transformation"
              :alt="item.alt"
              :title="item.title"
            />
          </figure>
          <div class="flex flex-col items-start px-4 py-2">
            <h3 class="text-xl font-medium">{{ item.title }}</h3>
            <span class="text-md">{{ item.subtitle }}</span>
          </div>
        </WebccLink>
      </div>
      <div class="absolute top-1/3 w-full sm:hidden">
        <div v-if="!atStart" class="absolute left-0 md:left-4">
          <WebccButton
            variant="white"
            icon="site/chevron-left-large"
            round
            @click="scrollLeft"
          />
        </div>
        <div v-if="!atEnd" class="absolute right-0 md:right-4">
          <WebccButton
            variant="white"
            icon="site/chevron-right-large"
            round
            @click="scrollRight"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { WebccLink } from '#components'

const trPrefix = 'www.components.views.start.StartHolidayIdeas.'

const transformations = {
  xs: 'tr:w-1024,h-576',
  sm: 'tr:w-640,h-360',
  md: 'tr:w-640,h-360',
  lg: 'tr:w-640,h-360',
  xl: 'tr:w-640,h-360',
}
defineProps<{
  block: Block<InspirationsBlockContent>
}>()

const atStart = ref(true)
const atEnd = ref(false)

const scroller: Ref<null | HTMLDivElement> = ref(null)
const scrolltarget: Ref<null | (typeof WebccLink)[]> = ref(null)

const screen = useScreen()

// @TODO Why fallback value does not exist in transformations? mistake?
const transformation = computed(
  () => transformations[screen.current] || 'tr:w-900,h-560',
)

onMounted(() => {
  if (scroller.value) {
    atStart.value = scroller.value.scrollLeft === 0
    atEnd.value =
      scroller.value.scrollLeft + scroller.value.offsetWidth ===
      scroller.value.scrollWidth
  }

  screen.onResize(() => {
    if (scroller.value) {
      atStart.value = scroller.value.scrollLeft === 0
      atEnd.value =
        scroller.value.scrollLeft + scroller.value.offsetWidth ===
        scroller.value.scrollWidth
    }
  })
})

async function toAll() {
  await navigateTo(useConfdata().links?.landingPageMain?.href)
}

function scroll() {
  nextTick(() => {
    if (scroller.value) {
      atStart.value = scroller.value?.scrollLeft === 0
      // Why is there a half-pixel at the end? Rounding to still disable the arrow
      atEnd.value =
        Math.round(scroller.value?.scrollLeft + scroller.value?.offsetWidth) >=
        scroller.value?.scrollWidth
    }
  })
}

function scrollLeft() {
  if (scroller.value && scrolltarget.value) {
    scroller.value.scrollLeft -= Math.min(
      scrolltarget.value[0].$el.clientWidth,
      document.documentElement.clientWidth,
    )
  }
}

function scrollRight() {
  if (scroller.value && scrolltarget.value) {
    scroller.value.scrollLeft += Math.min(
      scrolltarget.value[0].$el.clientWidth,
      document.documentElement.clientWidth,
    )
  }
}
</script>
